import { Formula } from '../../formulas/types/Formula'
import { Dimension } from '../../dimension/types/Dimension'
import { DriverData } from '../types/driverData'

/**
 * Generates a complete matrix of all formula × dimension combinations
 * @param formulas - Driver formulas (where isVariable === true)
 * @param dimensions - All dimensions
 * @returns Complete DriverData array with all combinations (without budget values)
 */
export const generateDriverDataMatrix = (formulas: Formula[], dimensions: Dimension[]): DriverData[] => {
  const matrix: DriverData[] = []

  formulas.forEach(formula => {
    dimensions.forEach(dimension => {
      matrix.push({
        formulaId: formula.id,
        dimensionId: dimension.dimensionId,
        variableType: formula.type,
        budget: undefined
      })
    })
  })

  return matrix
}

/**
 * Merges API data into the complete matrix
 * @param matrix - Complete matrix with all combinations
 * @param apiData - Data from API (may be partial)
 * @returns Merged data where API values override matrix placeholders
 */
export const mergeDriverData = (matrix: DriverData[], apiData: any[]): DriverData[] => {
  // Create a map of API data for quick lookup
  const apiDataMap = new Map<string, any>()
  apiData.forEach(row => {
    const key = `${row.formulaId}-${row.dimensionId}`
    apiDataMap.set(key, row)
  })

  // Merge API data into matrix
  return matrix.map(matrixRow => {
    const key = `${matrixRow.formulaId}-${matrixRow.dimensionId}`
    const apiRow = apiDataMap.get(key)

    // If API has data for this combination, use it
    if (apiRow) {
      // Transform API data structure to match DriverData interface
      // API returns: { elements, groups, length, start, end, ... }
      // We need: { budget: { elements, groups, length, start, end }, ... }
      const { elements, groups, length, start, end, dimensionId, formulaId, ...rest } = apiRow

      return {
        dimensionId: matrixRow.dimensionId, // Use dimensionId from matrix to preserve 'null' string
        formulaId: matrixRow.formulaId, // Ensure formulaId is number type from matrix
        variableType: matrixRow.variableType,
        budget:
          elements || groups
            ? {
                elements: elements || {},
                groups: groups || {},
                length: length || 0,
                start,
                end
              }
            : undefined,
        ...rest
      }
    }

    return matrixRow
  })
}
